/**
 * FR-112 mục 1 — *"ai được thêm tên miền"* — bảng **CHỈ ĐỌC** ở tab Chính sách.
 *
 * `OQ-037` đã chốt: mục này **không** có khoá `settings` riêng. Quyết định "ai
 * được thêm tên miền" đã có đúng một nguồn sự thật là capability `dom.add`
 * (`docs/05 §5.2`, danh mục 32 capability đóng), Admin bật/tắt ở `roles.html`
 * theo FR-061/FR-062. Thêm khoá thứ hai sẽ tạo hai cổng cho cùng một quyết
 * định — thứ `docs/05 §5.1` cấm. Nên ở đây chỉ **phản chiếu** trạng thái thật
 * và trỏ người đọc sang nơi sửa được.
 *
 * Vì sao có nhánh "không đủ quyền" thay vì gọi rồi để 403 rơi ra:
 * `settings.router.ts` gác `adm.settings`, còn `roles.router.ts` gác
 * `adm.roles` — hai capability độc lập. Một vai trò tuỳ biến chỉ có
 * `adm.settings` mở được tab này nhưng `GET /roles` trả **403** (có test
 * `apps/api/test/oq-140-policy-dom-add.test.ts`). Bắn một request chắc chắn
 * hỏng để hiện khung lỗi đỏ là nói dối người dùng: hệ thống không hỏng, họ chỉ
 * không có quyền đọc. Xem OQ-140.
 */

import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useT } from '../../lib/i18n';
import type { SystemRole } from '../../lib/types';
import { Icon } from '../../components/Icon';
import { AsyncSection, EmptyState, SkeletonCard } from '../../components/States';

/** `docs/05 §5.2` — id trong danh mục 32 capability đóng. */
const DOM_ADD = 'dom.add';

export function DomainAddPolicyCard() {
  const t = useT();
  const { can } = useAuth();
  const xemDuocVaiTro = can('adm.roles');

  const rolesQuery = useQuery({
    queryKey: ['roles'],
    queryFn: () => api.get<SystemRole[]>('/roles'),
    enabled: xemDuocVaiTro,
  });

  const coQuyenThem = (rolesQuery.data ?? []).filter((role) => role.capabilities.includes(DOM_ADD));

  return (
    <div className="card" data-od-id="policy-dom-add">
      <div className="card-head">
        <div className="grow">
          <h2>{t('settings.policy.domAdd.title')}</h2>
          <div className="sub">{t('settings.policy.domAdd.sub')}</div>
        </div>
        {xemDuocVaiTro ? (
          <Link className="btn btn-ghost" to="/admin/roles">
            <Icon name="shield" size={16} />
            {t('settings.policy.domAdd.manage')}
          </Link>
        ) : null}
      </div>

      <div className="card-body stack">
        {xemDuocVaiTro ? (
          <AsyncSection
            loading={rolesQuery.isPending}
            error={rolesQuery.error}
            data={coQuyenThem}
            onRetry={() => void rolesQuery.refetch()}
            skeleton={<SkeletonCard rows={3} />}
            empty={
              <EmptyState
                icon="shield"
                title={t('settings.policy.domAdd.noneTitle')}
                body={t('settings.policy.domAdd.noneBody')}
              />
            }
          >
            {(roles) => (
              /*
               * `.table-wrap` — OQ-211.
               *
               * Tôi KHÔNG đo được bảng này ở màn hẹp (jsdom không dựng hình, và
               * tôi không mở được trình duyệt). Nhưng nó có đúng thành phần đã
               * làm bảng Sao lưu tràn: ô đầu chứa tên vai trò cộng **hai** chip
               * `white-space: nowrap` nằm cùng dòng, nên nó có một bề rộng tối
               * thiểu cứng mà nội dung không tự co được.
               *
               * Bọc lại làm câu hỏi thành vô nghĩa thay vì phải trả lời: vừa
               * khung thì `overflow-x: auto` không vẽ gì, chật thì nó cuộn trong
               * khung riêng thay vì đẩy cả trang. Rẻ hơn một phép đoán.
               */
              <div className="table-wrap">
                <table className="tbl" data-od-id="policy-dom-add-table">
                  <thead>
                    <tr>
                      <th scope="col">{t('settings.policy.domAdd.colRole')}</th>
                      <th scope="col">{t('settings.policy.domAdd.colUsers')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {roles.map((role) => (
                      <tr key={role.id}>
                        <td>
                          <b>{role.name}</b>
                          {role.isSystem ? (
                            <span className="chip" style={{ marginInlineStart: 8 }}>
                              {t('settings.policy.domAdd.system')}
                            </span>
                          ) : null}
                          {role.isDefault ? (
                            <span className="chip" style={{ marginInlineStart: 8 }}>
                              {t('settings.policy.domAdd.default')}
                            </span>
                          ) : null}
                          <div className="sub mono">{role.slug}</div>
                        </td>
                        <td className="muted">{t('roles.userCount', { count: role.userCount })}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </AsyncSection>
        ) : (
          <EmptyState
            icon="shield"
            title={t('settings.policy.domAdd.needRolesTitle')}
            body={t('settings.policy.domAdd.needRolesBody')}
          />
        )}

        <p className="sub">{t('settings.policy.domAdd.foot')}</p>
      </div>
    </div>
  );
}
